export interface Vec2 {
  x: number;
  z: number;
}

export const vec2 = (x = 0, z = 0): Vec2 => ({ x, z });

export const cloneVec2 = (v: Vec2): Vec2 => ({ x: v.x, z: v.z });

export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, z: a.z + b.z });

export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, z: a.z - b.z });

export const scale = (v: Vec2, s: number): Vec2 => ({ x: v.x * s, z: v.z * s });

export const lengthSq = (v: Vec2): number => v.x * v.x + v.z * v.z;

export const length = (v: Vec2): number => Math.sqrt(lengthSq(v));

export const distance = (a: Vec2, b: Vec2): number => length(sub(a, b));

export const distanceSq = (a: Vec2, b: Vec2): number => lengthSq(sub(a, b));

export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.z * b.z;

export function normalize(v: Vec2): Vec2 {
  const len = length(v);
  return len > 1e-6 ? { x: v.x / len, z: v.z / len } : { x: 0, z: 0 };
}

export const clamp = (v: number, min: number, max: number): number =>
  v < min ? min : v > max ? max : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Angle measured from +Z towards +X, matching `Math.atan2(x, z)`. */
export const angleOf = (v: Vec2): number => Math.atan2(v.x, v.z);

export const fromAngle = (angle: number, len = 1): Vec2 => ({
  x: Math.sin(angle) * len,
  z: Math.cos(angle) * len,
});

export function wrapAngle(angle: number): number {
  let a = angle;
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export const angleDelta = (a: number, b: number): number => wrapAngle(b - a);

export function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + angleDelta(a, b) * t);
}

/** Signed shortest angle from `from` to `to`, in radians. */
export const angleTo = (from: Vec2, to: Vec2): number => angleOf(sub(to, from));

export function withinArc(
  origin: Vec2,
  facing: number,
  target: Vec2,
  range: number,
  arc: number,
): boolean {
  const toTarget = sub(target, origin);
  const dist = length(toTarget);
  if (dist > range) return false;
  if (dist < 1e-4) return true;
  const delta = Math.abs(angleDelta(facing, angleOf(toTarget)));
  return delta <= arc * 0.5;
}

export function moveTowards(current: Vec2, target: Vec2, maxDelta: number): Vec2 {
  const delta = sub(target, current);
  const dist = length(delta);
  if (dist <= maxDelta || dist < 1e-6) return cloneVec2(target);
  return add(current, scale(delta, maxDelta / dist));
}
